import { HubFeature } from '@ats/api/cm';
import { LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';
import {
	CPCreqHistoryEnrollmentSharedLocNames,
	creqEnrollmentSharedLocs,
} from '../../models/enrollment.locs';

type CPCReqHistoryEnrollmentDetailLocNames =
	| 'CPCReqHistoryEnrollmentDetailsPageTitle'
	| 'CPCReqHistoryEnrollmentDetailsPageDescription'
	| 'CPCReqHistoryEnrollmentDetailsButtonBack'
	| 'CPCReqHistoryEnrollmentDetailsButtonCancel'
	| 'CPCReqHistoryEnrollmentDetailsButtonEdit'
	| 'CPCReqHistoryEnrollmentDetailsButtonInfo'
	| 'CPCReqHistoryEnrollmentDetailsButtonUpdate'
	| 'CPCReqHistoryEnrollmentDetailsFieldErrorDocLanguageInvalid'
	| 'CPCReqHistoryEnrollmentDetailsFieldErrorDocLanguageRequired'
	| 'CPCReqHistoryEnrollmentDetailsFieldErrorDocumentationLimit'
	| 'CPCReqHistoryEnrollmentDetailsFieldErrorDocumentationRequired'
	| 'CPCReqHistoryEnrollmentDetailsFieldErrorDocumentationSize'
	| 'CPCReqHistoryEnrollmentDetailsFieldLabelActiveDate'
	| 'CPCReqHistoryEnrollmentDetailsFieldLabelDataProvider'
	| 'CPCReqHistoryEnrollmentDetailsFieldLabelDocumentation'
	| 'CPCReqHistoryEnrollmentDetailsFieldLabelDocumentationLanguage'
	| 'CPCReqHistoryEnrollmentDetailsFieldLabelExpireDate'
	| 'CPCReqHistoryEnrollmentDetailsFieldLabelInvalid'
	| 'CPCReqHistoryEnrollmentDetailsFieldLabelNotes'
	| 'CPCReqHistoryEnrollmentDetailsFieldLabelProvider'
	| 'CPCReqHistoryEnrollmentDetailsFieldLabelReviewState'
	| 'CPCReqHistoryEnrollmentDetailsFieldLabelUpdated'
	| 'CPCReqHistoryEnrollmentDetailsNotificationMessageUpdateSuccess'
	| CPCreqHistoryEnrollmentSharedLocNames;

export const creqEnrollmentDetailLocs: LocDefs<CPCReqHistoryEnrollmentDetailLocNames> = {
	// Details Page
	CPCReqHistoryEnrollmentDetailsPageTitle: {
		defaultValue: 'Enrollment Details',
		description: 'Page title for: Enrollment Details.',
		displayPath: 'History|Enrollment|Details|Title',
		portal: 'CP',
		displayType: 'Title',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.Title.PageTitle',
	},
	CPCReqHistoryEnrollmentDetailsPageDescription: {
		defaultValue: '',
		description: 'Page description for: Enrollment Details.',
		displayPath: 'History|Enrollment|Details|Description',
		portal: 'CP',
		displayType: 'Content',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: true,
		shared: false,
	},

	// Details Buttons
	CPCReqHistoryEnrollmentDetailsButtonBack: {
		defaultValue: 'Back',
		description: 'Button label for: Back.',
		displayPath: 'History|Enrollment|Details|Back',
		portal: 'CP',
		displayType: 'Button',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.Button.Back',
	},
	CPCReqHistoryEnrollmentDetailsButtonCancel: {
		defaultValue: 'Cancel',
		description: 'Button label for: Cancel.',
		displayPath: 'History|Enrollment|Details|Cancel',
		portal: 'CP',
		displayType: 'Button',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.Button.Cancel',
	},
	CPCReqHistoryEnrollmentDetailsButtonEdit: {
		defaultValue: 'Edit',
		description: 'Button label for: Edit.',
		displayPath: 'History|Enrollment|Details|Edit',
		portal: 'CP',
		displayType: 'Button',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.Button.Edit',
	},
	CPCReqHistoryEnrollmentDetailsButtonInfo: {
		defaultValue: 'Info',
		description: 'Button label for: Info.',
		displayPath: 'History|Enrollment|Details|Info',
		portal: 'CP',
		displayType: 'Button',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.Button.Info',
	},
	CPCReqHistoryEnrollmentDetailsButtonUpdate: {
		defaultValue: 'Update',
		description: 'Button label for: Update.',
		displayPath: 'History|Enrollment|Details|Update',
		portal: 'CP',
		displayType: 'Button',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.Button.Update',
	},

	// Details Field Errors
	CPCReqHistoryEnrollmentDetailsFieldErrorDocLanguageInvalid: {
		defaultValue: 'Invalid documentation language',
		description: 'Error message for invalid documentation language.',
		displayPath: 'History|Enrollment|Details|DocLanguageInvalid',
		portal: 'CP',
		displayType: 'Error',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.FieldError.DocLanguageInvalid',
	},
	CPCReqHistoryEnrollmentDetailsFieldErrorDocLanguageRequired: {
		defaultValue: 'Documentation language is required',
		description: 'Error message for required documentation language.',
		displayPath: 'History|Enrollment|Details|DocLanguageRequired',
		portal: 'CP',
		displayType: 'Error',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.FieldError.DocLanguageRequired',
	},
	CPCReqHistoryEnrollmentDetailsFieldErrorDocumentationLimit: {
		defaultValue: 'Documentation limit exceeded',
		description: 'Error message for documentation limit.',
		displayPath: 'History|Enrollment|Details|DocLimit',
		portal: 'CP',
		displayType: 'Error',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.FieldError.DocumentationLimit',
	},
	CPCReqHistoryEnrollmentDetailsFieldErrorDocumentationRequired: {
		defaultValue: 'Documentation is required',
		description: 'Error message for required documentation.',
		displayPath: 'History|Enrollment|Details|DocRequired',
		portal: 'CP',
		displayType: 'Error',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.FieldError.DocumentationRequired',
	},
	CPCReqHistoryEnrollmentDetailsFieldErrorDocumentationSize: {
		defaultValue: 'Documentation size exceeds limit',
		description: 'Error message for documentation size.',
		displayPath: 'History|Enrollment|Details|DocSize',
		portal: 'CP',
		displayType: 'Error',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.FieldError.DocumentationSize',
	},

	// Details Field Labels
	CPCReqHistoryEnrollmentDetailsFieldLabelActiveDate: {
		defaultValue: 'Active Date',
		description: 'Field label for: Active Date.',
		displayPath: 'History|Enrollment|Details|ActiveDate',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.FieldLabel.ActiveDate',
	},
	CPCReqHistoryEnrollmentDetailsFieldLabelDataProvider: {
		defaultValue: 'Data Provider',
		description: 'Field label for: Data Provider.',
		displayPath: 'History|Enrollment|Details|DataProvider',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.FieldLabel.DataProvider',
	},
	CPCReqHistoryEnrollmentDetailsFieldLabelDocumentation: {
		defaultValue: 'Documentation',
		description: 'Field label for: Documentation.',
		displayPath: 'History|Enrollment|Details|Documentation',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.FieldLabel.Documentation',
	},
	CPCReqHistoryEnrollmentDetailsFieldLabelDocumentationLanguage: {
		defaultValue: 'Documentation Language',
		description: 'Field label for: Documentation Language.',
		displayPath: 'History|Enrollment|Details|DocLanguage',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.FieldLabel.DocumentationLanguage',
	},
	CPCReqHistoryEnrollmentDetailsFieldLabelExpireDate: {
		defaultValue: 'Expire Date',
		description: 'Field label for: Expire Date.',
		displayPath: 'History|Enrollment|Details|ExpireDate',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.FieldLabel.ExpireDate',
	},
	CPCReqHistoryEnrollmentDetailsFieldLabelInvalid: {
		defaultValue: 'Invalid',
		description: 'Field label for: Invalid.',
		displayPath: 'History|Enrollment|Details|Invalid',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.FieldLabel.Invalid',
	},
	CPCReqHistoryEnrollmentDetailsFieldLabelNotes: {
		defaultValue: 'Notes',
		description: 'Field label for: Notes.',
		displayPath: 'History|Enrollment|Details|Notes',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.FieldLabel.Notes',
	},
	CPCReqHistoryEnrollmentDetailsFieldLabelProvider: {
		defaultValue: 'Provider',
		description: 'Field label for: Provider.',
		displayPath: 'History|Enrollment|Details|Provider',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.FieldLabel.Provider',
	},
	CPCReqHistoryEnrollmentDetailsFieldLabelReviewState: {
		defaultValue: 'Review Status',
		description: 'Field label for: Review State.',
		displayPath: 'History|Enrollment|Details|ReviewState',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.FieldLabel.ReviewState',
	},
	CPCReqHistoryEnrollmentDetailsFieldLabelUpdated: {
		defaultValue: 'Updated',
		description: 'Field label for: Updated.',
		displayPath: 'History|Enrollment|Details|Updated',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.FieldLabel.Updated',
	},

	// Details Notification
	CPCReqHistoryEnrollmentDetailsNotificationMessageUpdateSuccess: {
		defaultValue: 'Update successful',
		description: 'Notification message for successful update.',
		displayPath: 'History|Enrollment|Details|UpdateSuccess',
		portal: 'CP',
		displayType: 'Notification',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'OtherRequirement/History.Enrollment.Details.NotificationMessage.UpdateSuccess',
	},
	...creqEnrollmentSharedLocs,
};

export const creqEnrollmentDetailPageLocs: Pick<
	LocMap<CPCReqHistoryEnrollmentDetailLocNames>,
	'CPCReqHistoryEnrollmentDetailsPageTitle' | 'CPCReqHistoryEnrollmentDetailsPageDescription'
> = {
	CPCReqHistoryEnrollmentDetailsPageTitle: 'CPCReqHistoryEnrollmentDetailsPageTitle',
	CPCReqHistoryEnrollmentDetailsPageDescription: 'CPCReqHistoryEnrollmentDetailsPageDescription',
};

export const cpCreqEnrollmentDetailLocKeys =
	MapLocKeys<CPCReqHistoryEnrollmentDetailLocNames>(creqEnrollmentDetailLocs);

export type CPCreqHistoryEnrollmentDetailLocKeys = typeof cpCreqEnrollmentDetailLocKeys;
